import React, { useEffect } from "react";

import _ from "lodash";
import moment from "moment";

import {
  AlertChart,
  type TimeSeriesWithColor,
} from "@components/reports/charts";
import { TitleSlide } from "@components/title-slide-view";
import { useNorthbeamQuery } from "@hooks/http";
import {
  type Preview,
  type PreviewVariables,
} from "@north-beam/nb-client-side";
import {
  type AlertPreviewRowV2,
  jsonHash,
  todayLocalAsISODate,
  type TriggerConditionsV2,
  type metrics as metricsPkg,
} from "@north-beam/nb-common";
import { spaceColorScheme } from "@pages/objects/label-colors";
import { PrimaryButton } from "@shared/buttons";
import { Select } from "@shared/selects";
import { type ReactSelectOption } from "@utils/index";

import { PREVIEW } from "../queries";

import { type AlertConfigState } from "./alert-config-editor";
import { makeSeriesBySubjectItem } from "./alert-preview-chart-viewer";
import { AlertRunTable } from "./alert-run-table";
import { flattenAlarmCondition, flattenResultRows } from "./utils";

export interface AlertSimulatePaneProps {
  preview: AlertConfigState | undefined;
  id?: string;
}

export function AlertSimulatePane(props: AlertSimulatePaneProps) {
  const { preview, id } = props;

  const [current, setCurrent] = React.useState<AlertConfigState | undefined>(
    preview,
  );

  const date = todayLocalAsISODate();

  const variables = React.useMemo(() => {
    if (!current) {
      return undefined;
    }
    const triggerConditions: TriggerConditionsV2 = {
      version: "v2",
      alarmCondition: current.alarmCondition,
      subject: current.subject,
    };
    return {
      triggerConditions,
      date,
      days: 7,
      configId: id,
    };
  }, [current, id, date]);

  const { loading, data } = useNorthbeamQuery<Preview, PreviewVariables>(
    PREVIEW,
    {
      variables,
      skip: !current,
    },
  );

  const response = data?.me?.previewCustomAlertConfig;

  const previews: AlertPreviewRowV2[] | undefined = React.useMemo(() => {
    if (response) {
      return response.previews;
    }
  }, [response]);

  const allChoices = React.useMemo(() => {
    if (!current || !previews) {
      return;
    }

    return parsePreviews(previews);
  }, [current, previews]);

  /* HACK: alarmCondition is a tree-like structure, and so is previews.resultRows.
     HACK: they, in fact, have the same tree-like structure.
     HACK: we exploit this by flattening both trees and using numeric indices to cross
     HACK: reference the correct SimpleAlarmConditionV2 to every SimpleAlarmConditionResultRow. */
  const simpleAlarmConditions = React.useMemo(
    () => (current ? flattenAlarmCondition(current.alarmCondition) : undefined),
    [current],
  );
  const metrics = React.useMemo(() => {
    if (simpleAlarmConditions) {
      return simpleAlarmConditions.map((v, index) => ({
        ...v.metric,
        index,
      }));
    }
  }, [simpleAlarmConditions]);

  const [selectedMetricIndex, setSelectedMetricIndex] =
    React.useState<number>(0);

  const [selectedChoice, setSelectedChoice] = React.useState<string | null>(
    null,
  );

  useEffect(() => {
    if (allChoices?.length) {
      setSelectedChoice(allChoices[0].value);
    }
  }, [allChoices]);

  const subjectItemToColor = React.useMemo(() => {
    if (previews) {
      return makeSubjectItemHashToColor(previews);
    }
  }, [previews]);

  const byDate: AlertPreviewRowV2[][] | undefined = React.useMemo(() => {
    if (previews && selectedChoice) {
      return _.chain(previews)
        .filter((v) => jsonHash(v.subjectItem) === selectedChoice)
        .groupBy("runAt")
        .values()
        .sortBy((v) => v[0].runAt)
        .reverse()
        .value();
    }
  }, [selectedChoice, previews]);

  const resultRows = React.useMemo(() => {
    if (previews && selectedChoice) {
      return _.chain(previews)
        .filter((v) => jsonHash(v.subjectItem) === selectedChoice)
        .map((p) => {
          const rows = flattenResultRows(p.resultRow);
          return rows[selectedMetricIndex] ?? rows[0];
        })
        .value();
    }
  }, [selectedMetricIndex, selectedChoice, previews]);

  const allSerieses = React.useMemo(() => {
    if (
      current &&
      subjectItemToColor &&
      metrics &&
      resultRows &&
      simpleAlarmConditions
    ) {
      return makeSeriesBySubjectItem(
        subjectItemToColor,
        resultRows,
        metrics[selectedMetricIndex],
        simpleAlarmConditions[selectedMetricIndex],
      );
    }
  }, [
    subjectItemToColor,
    resultRows,
    metrics,
    selectedMetricIndex,
    current,
    simpleAlarmConditions,
  ]);

  const serieses = React.useMemo(() => {
    if (allSerieses && selectedChoice) {
      return [allSerieses[selectedChoice]].filter((v) => !!v);
    }
  }, [selectedChoice, allSerieses]);

  return (
    <div>
      <div className="bg-white p-4 border-b">
        <div className="flex items-end space-x-2 max-w-4xl">
          <Select
            label="Metric"
            isLoading={loading}
            isDisabled={!metrics}
            options={
              metrics?.map((metric) => ({
                label: metric.name,
                value: metric,
              })) ?? []
            }
            value={metrics?.[selectedMetricIndex ?? 0] ?? null}
            onChange={(value: any) => setSelectedMetricIndex(value.index)}
          />
          <Select
            label="Subject"
            isLoading={loading}
            isDisabled={!allChoices}
            options={allChoices ?? []}
            value={selectedChoice}
            onChange={setSelectedChoice}
          />
          <PrimaryButton
            disabled={jsonHash(current) === jsonHash(preview) && !loading}
            onClick={() => setCurrent(preview)}
          >
            UPDATE SIMULATION
          </PrimaryButton>
        </div>
      </div>
      <div className="mx-4 mt-4">
        {!current ? (
          <TitleSlide className="mt-5">
            <p>
              When you&apos;ve filled out the alert parameters, we&apos;ll show
              you here whether you would have received them in the past.
            </p>
          </TitleSlide>
        ) : loading ? (
          <TitleSlide>Loading simulation...</TitleSlide>
        ) : selectedChoice ? (
          <InnerInner
            serieses={serieses!}
            metric={metrics![selectedMetricIndex]}
            byDate={byDate!}
            state={current}
          />
        ) : (
          <TitleSlide className="mt-5">
            This alert is not monitoring any subjects. Please update the alert
            parameters!
          </TitleSlide>
        )}
      </div>
    </div>
  );
}

function InnerInner({
  metric,
  serieses,
  byDate,
  state,
}: {
  metric: metricsPkg.MetricFragment;
  serieses: TimeSeriesWithColor[];
  byDate: AlertPreviewRowV2[][];
  state: AlertConfigState;
}) {
  return (
    <>
      <div className="bg-white rounded-md border p-4 my-4">
        <AlertChart serieses={serieses} format={metric.format} height={300} />
      </div>
      {byDate.map((ps) => {
        const runAt = ps[0].runAt;
        return (
          <div key={runAt}>
            <h5 className="font-bold">{moment(runAt).format("MMM D, YYYY")}</h5>
            <hr />
            <AlertRunTable
              runAt={runAt}
              results={ps.map((context) => ({ context }))}
              alarmCondition={state.alarmCondition}
            />
          </div>
        );
      })}
    </>
  );
}

function makeSubjectItemHashToColor(previews: any[]) {
  const subjectItemHashes = _.chain(previews)
    .map((v) => [jsonHash(v.subjectItem), 1])
    .fromPairs()
    .keys()
    .sort()
    .value();
  const colors = spaceColorScheme(subjectItemHashes.length);

  return _.fromPairs(_.zip(subjectItemHashes, colors));
}

function parsePreviews(
  previewses: AlertPreviewRowV2[],
): ReactSelectOption<string>[] {
  const serieses = _.chain(previewses)
    .groupBy((p) => jsonHash(p.subjectItem))
    .value();

  const keys = Object.keys(serieses).sort();
  const rv = keys.map((key) => {
    const name = serieses[key][0].subjectItemName;
    const numAlarms = _.filter(
      serieses[key],
      (v) => v.conditionTriggered,
    ).length;
    const numSkips = _.filter(serieses[key], (v) => !!v.skipReason).length;

    let label = name;
    if (numAlarms) {
      label = `⚠️ ${label}`;
    } else if (numSkips === serieses[key].length) {
      label = `🔵 ${label}`;
    } else {
      label = `⚪ ${label}`;
    }

    return {
      value: key,
      label,
      numAlarms,
      numSkips,
    };
  });

  return _.sortBy(rv, (v) => -(1000 * v.numAlarms + -v.numSkips));
}
